/**
 * Aklaim brand behaviours for Horizon.
 * - Scroll reveals: elements with .ak-reveal get .is-in when they enter the viewport.
 *   Stagger with style="--ak-delay: 120ms". The theme editor and reduced motion skip this in CSS.
 * - Poster stack tilt: .ak-stack follows the pointer a few degrees on hover-capable devices.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const canHover = window.matchMedia('(hover: hover)');

function initReveals(root = document) {
  const targets = root.querySelectorAll('.ak-reveal:not(.is-in)');
  if (!targets.length) return;

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  targets.forEach((el) => io.observe(el));
}

function initTilt(root = document) {
  if (!canHover.matches || reduceMotion.matches) return;

  root.querySelectorAll('.ak-stack[data-tilt]').forEach((stack) => {
    if (stack.dataset.tiltReady) return;
    stack.dataset.tiltReady = 'true';

    let frame = 0;
    const onMove = (event) => {
      const rect = stack.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        stack.style.setProperty('--ak-tx', `${(px * 10).toFixed(2)}deg`);
        stack.style.setProperty('--ak-ty', `${(-py * 8).toFixed(2)}deg`);
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(frame);
      stack.style.removeProperty('--ak-tx');
      stack.style.removeProperty('--ak-ty');
    };

    stack.addEventListener('pointermove', onMove);
    stack.addEventListener('pointerleave', onLeave);
  });
}

function init(root) {
  initReveals(root);
  initTilt(root);
  initHoverPreload(root);
}

init(document);

// Horizon re-renders sections in the editor and on page transitions; pick those up too.
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('page:transition:end', () => init(document));
new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType === 1 && (node.matches?.('.ak-reveal, .ak-stack, product-card') || node.querySelector?.('.ak-reveal, .ak-stack, product-card'))) {
        init(node);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/**
 * Hover-image preload. Horizon only kicks the second card image once the slideshow has been
 * hovered, which shows as a white flash on the first hover. Fetch the next slide's image as
 * soon as the card is near the viewport instead.
 */
function initHoverPreload(root = document) {
  if (!canHover.matches || !('IntersectionObserver' in window)) return;

  const cards = root.querySelectorAll('product-card:not([data-ak-preload])');
  if (!cards.length) return;

  const kick = (card) => {
    const slides = card.querySelectorAll('slideshow-slide');
    if (slides.length < 2) return;
    const current = card.querySelector('slideshow-slide[aria-hidden="false"]') || slides[0];
    const next = current.nextElementSibling || slides[1];
    next?.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        kick(entry.target);
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '200px 0px' }
  );

  cards.forEach((card) => {
    card.dataset.akPreload = 'true';
    io.observe(card);
  });
}
